import React, { forwardRef, useCallback, useEffect, useState } from 'react';
import api from '~/services/api';
import { AlertBox } from './styles';
import { InputSelect } from '~/components/NovosInputs';

interface Fornecedor {
  cod_pessoa: number;
  cod_fornecedor: number;
  des_fornecedor: string;
  num_cpf_cnpj: string;
  flg_inativo?: boolean;
}

interface Select {
  label: string;
  value: number;
  flg_inativo?: boolean;
}

interface BuscaFornecedorProps {
  /** Dispara quando uma opção é escolhida (value/label) */
  onChange: (e: any) => void;
  placeholder?: string;
  label?: string;
  invalid?: boolean;
  register: any;
  name: string;
  control: any;

  /**
   * Valor para pré-selecionar fornecedor assim que a lista carregar.
   * Deve corresponder ao `value` (cod_fornecedor).
   * Ex.: 123 ou "123".
   */
  retriever?: number | string;
}

/**
 * BuscaFornecedor
 *
 * - Carrega fornecedores da API e permite filtrar por texto.
 * - Se `retriever` for informado, pré-seleciona a opção cujo `value === retriever`
 *   assim que os dados estiverem disponíveis (sem depender do filtro).
 * - Observação: a pré-seleção chama `onChange({ value, label })`,
 *   deixando a sincronização visual para o formulário/control pai (via RHF).
 */
export const BuscaFornecedor: React.FC<BuscaFornecedorProps> = forwardRef(
  (props) => {
    const {
      label,
      invalid = false,
      placeholder = 'Digite para buscar o Fornecedor',
      onChange,
      register,
      control,
      name,
      retriever,
    } = props;

    const [isInvalid, setIsInvalid] = useState<boolean>(false);
    const [store, setStore] = useState<Select[]>([]);
    const [storeFiltered, setStoreFiltered] = useState<Select[]>([]);

    useEffect(() => {
      setIsInvalid(invalid);
    }, [invalid]);

    const addAlert = useCallback((): string => {
      return isInvalid ? 'alert-class' : '';
    }, [isInvalid]);

    // Normaliza o id para comparação estrita (ex.: "42" -> 42)
    const normalizeId = useCallback((v: unknown) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : v;
    }, []);

    const getFornecedores = useCallback(async () => {
      const res = await api.get('/fornecedor/buscaFornecedor');
      const { data } = res.data;

      const options: Select[] = (data as Fornecedor[]).map((f) => ({
        label: `${f.des_fornecedor}`,
        value: f.cod_fornecedor,
        flg_inativo: f.flg_inativo,
      }));

      setStore(options);
      setStoreFiltered([]);
    }, []);

    useEffect(() => {
      getFornecedores();
    }, [getFornecedores]);

    /**
     * Pré-seleção via `retriever`:
     * - Procura no conjunto completo (store), ignorando o filtro.
     * - Chama `onChange({ value, label })` quando encontra.
     *
     * Observação: se quiser reproduzir *exatamente* a limitação do outro componente
     * (não reagir a mudanças dinâmicas de `retriever`), remova `retriever` das deps.
     */
    useEffect(() => {
      if (!retriever || store.length === 0) return;

      const targetId = normalizeId(retriever);
      const found = store.find(
        (opt) =>
          opt.flg_inativo !== true && normalizeId(opt.value) === targetId,
      );

      if (found) {
        onChange({ value: found.value, label: found.label });
      }
    }, [retriever, store, normalizeId, onChange]);

    const handleFilter = useCallback(
      (term: string) => {
        if (term === '') {
          setStoreFiltered([]);
          return;
        }

        const filtered = store.filter(
          (d) => d.label.toUpperCase().indexOf(term.toUpperCase()) > -1,
        );

        const onlyActives = filtered.filter((f) => f.flg_inativo !== true);
        setStoreFiltered(onlyActives);
      },
      [store],
    );

    return (
      <AlertBox>
        <div className={addAlert()}>
          <InputSelect
            label={label}
            options={storeFiltered as any[]}
            placeholder={placeholder}
            register={register}
            control={control}
            onInput={(ev: any) => {
              const val = ev.target.value as string;
              handleFilter(val);
            }}
            name={name}
            isError={invalid}
            changeSelected={(selected: any) => {
              onChange(selected);
            }}
          />
        </div>
      </AlertBox>
    );
  },
);
